import * as React from 'react';
import {
  Badge,
  LinearProgress,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment';
import i18next from 'i18next';
import { Link } from 'react-router-dom';
import { dataGridSx } from '../../../style/globalStyle';
import _ from 'lodash';
import { Context } from '../../../states/Store';
import useStyles from '../../../style/js-style/components/common/datatable/EADataGridStyle';
import EAIcon from '../EAIcon';
import { useContext } from 'react';
import {
  GridToolbarQuickFilter,
  gridVisibleRowCountSelector,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid';
import { dataGridTranslation, sheetStyle } from '../../../services/common/DataGridUtils';
import EAButton from '../EAButton';
import clsx from 'clsx';
import {
  DataGridPremium,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import EADataGridExport from './EADataGridExport';
import EAColorIcon from '../EAColorIcon';

const EAGridLink = ({ link, children }) => {
  const { classes } = useStyles();

  return link ? (
    <Link to={link} className={classes.linkCell}>
      {children}
    </Link>
  ) : (
    <span>{children}</span>
  );
};

const EAGridCallBack = ({ callBack, children, disableTranslation = false }) => {
  const { classes } = useStyles();

  return callBack ? (
    <div onClick={callBack} className={clsx(classes.linkCell, (disableTranslation ? 'dt' : ""))}>
      {children}
    </div>
  ) : (
    <span className={clsx((disableTranslation ? 'dt' : ""))}>{children}</span>
  );
};

const EADataGridCellType = ({type, action, children, callBack}) => {
  const { classes } = useStyles();
  return type === "link" ? <EAGridLink link={action}>{children}</EAGridLink>
    : type === "callBack" ? <EAGridCallBack callBack={callBack}>{children}</EAGridCallBack>
    : <div className={classes.linkCell} onClick={action}>{children}</div>
}
const isGridCellTypeEqual = (prevProps, nextProps) => {
  if(prevProps.type !== nextProps.type) return false;
  if(prevProps.action?.toString() !== nextProps.action?.toString()) return false;

  if (typeof prevProps.children === 'string') {
    return prevProps.children === nextProps.children
  };

  if (React.isValidElement(prevProps.children) ) {
    return isGridCellTypeEqual(prevProps.children.props, nextProps.children.props);
  }
  return false;
}
const MemoizedEADataGridCellType = React.memo(EADataGridCellType, isGridCellTypeEqual);

export const EADataGridCell = ({
  link,
  type = 'link',
  tooltip = false,
  tooltipLabel,
  children,
  callBack = () => {}
}) => {
  return (
    <MemoizedEADataGridCellType type={type} action={link} callBack={callBack}>
      {tooltip ? (
        <Tooltip title={tooltipLabel || children || ''}>
          <Typography
            style={{ fontSize: "0.875rem" }}
            noWrap
            component={'span'}
          >
            {children}
          </Typography>
        </Tooltip>
      ) : (
        children
      )}
    </MemoizedEADataGridCellType>
  );
};

export const cellWithTooltip = (value, array = false) => {
  // If it is an array, formats the text
  value = array
    ? value.map((v, index) => (
      <React.Fragment key={index}>
        {v}
        {index !== value.length - 1 && ', '}
      </React.Fragment>
    ))
    : value;

  return (
    <Tooltip title={value || ""}>
      <Typography style={{ fontSize: "0.875rem" }} noWrap component={"span"}>
        {value}
      </Typography>
    </Tooltip>
  );
};

export const dateValueFormatter = (params, link) => {
  return (
    <EAGridLink link={link}>
      {params.value
        ? moment(params.value).format(i18next.t("date.dateFormat"))
        : ""}
    </EAGridLink>
  );
};

function CustomToolbar({
  setColumnsButtonEl,
  setFilterButtonEl,
  setFilterActive,
  setAnswerFilterActive,
  isAnswerFilterActive,
  additionnalsComponents,
  additionnalsLeftComponents,
  answerBadgeCount,
  showAnswerFilter,
  hasFilters,
  externalExportHandler,
  quickFilter,
  showExport,
  noFilter = false,
}) {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();

  React.useEffect(() => {
    const unsubscribe = apiRef.current.subscribeEvent(
      "columnHeaderDragStart",
      (params, event) => {
        event.dataTransfer.effectAllowed = "move";
      }
    );
    return () => unsubscribe?.();
  }, [apiRef]);

  const exportStartIcon = () => {
    return <EAIcon icon={'export'} alt={'export icon'} />;
  };

  return noFilter ? (
    <></>
  ) : (
    <GridToolbarContainer className={classes.toolBarContainer}>
      <div className={classes.additionalLeft}>
        {additionnalsLeftComponents}
      </div>
      <div className={classes.filterContainer}>
        {quickFilter ? (
          <GridToolbarQuickFilter />
        ) : (
          <>
            {hasFilters && (
              <GridToolbarFilterButton
                className={classes.toolBarFilterButton}
                ref={setFilterButtonEl}
                componentsProps={{
                  button: {
                    onClick: () => {
                      setFilterActive(true);
                    },
                  },
                }}
              />
            )}
            {/* TODO: Pagination and Filters ServerSide */}
            {showAnswerFilter && (
              <div>
                <EAButton
                  className={clsx(
                    classes.toolBarFilterButton,
                    classes.toolBarFilterAnswerButton,
                    isAnswerFilterActive
                      ? classes.toolBarFilterAnswerButtonActive
                      : classes.toolBarFilterAnswerButtonInactive
                  )}
                  size={'small'}
                  disabled={answerBadgeCount === 0}
                  content={
                    <div className={classes.toolBarFilterAnswerButtonContent}>
                      <EAColorIcon
                        icon={
                          isAnswerFilterActive
                            ? 'filter_white'
                            : 'filter_primary'
                        }
                        svgStyle={{ position: 'relative', top: '1px' }}
                      />
                      {i18next.t('answer')}
                    </div>
                  }
                  onClick={() => {
                    setAnswerFilterActive(!isAnswerFilterActive);
                  }}
                ></EAButton>
                <Badge
                  badgeContent={answerBadgeCount}
                  className={classes.countBadge}
                  color="primary"
                  overlap="rectangular"
                />
              </div>
            )}

            <GridToolbarColumnsButton
              className={classes.toolBarColumnsButton}
              ref={setColumnsButtonEl}
              onClick={() => {
                setFilterActive(false);
              }}
            />
            <GridToolbarDensitySelector
              className={classes.toolBarDensitySelector}
            />
            {/*TODO: protect using mass actions privileges*/}
            {externalExportHandler ? (
              <EADataGridExport exportHandler={externalExportHandler} />
            ) : showExport ? (
              <GridToolbarExport
                excelOptions={{
                  exceljsPreProcess,
                  exceljsPostProcess,
                }}
                csvOptions={{ utf8WithBom: true, delimiter: ';' }}
                className={classes.toolBarExport}
                startIcon={exportStartIcon()}
              />
            ) : (
              <></>
            )}
          </>
        )}
        {additionnalsComponents}
      </div>
    </GridToolbarContainer>
  );
}

function defaultLabelDisplayedRows({ from, to, count }) {
  return (
    <span style={{ color: '#656575' }}>
      <b>{from}</b>&nbsp;
      <span>{i18next.t('dataGrid.to')}</span>&nbsp;
      <span>{to}</span>&nbsp;
      <span>{i18next.t('dataGrid.on')}</span>&nbsp;
      {count !== -1 ? <>
        <b>{count}</b>&nbsp;
      </> : <>
        <b>{i18next.t('dataGrid.moreThan')}</b>&nbsp;
        <b>{to}</b>
      </>}
    </span>
  );
}

function exceljsPreProcess({ worksheet }) {
  // Change all dates format
  worksheet.columns?.forEach(col => {
    if(col.style?.numFmt === 'dd.mm.yyyy') {
      col.style.numFmt = 'dd/mm/yyyy'
    }
  });
}

function exceljsPostProcess({ workbook, worksheet }) {
  // Add NotSpecified text for number cells that are empty.
  const rowCount = worksheet.rowCount;
  const attrColsWithEmptyCells = worksheet.columns?.filter(col => col.key?.startsWith('attribute-') && col.values?.length < rowCount);
  attrColsWithEmptyCells?.forEach(col => {
    const newValues = new Array(rowCount).fill(i18next.t("notSpecified"));
    col.values?.forEach((val, index) => {
      newValues[index-1] = val;
    })
    col.values = newValues;
  })
}

export default function EADataGrid({
  loading,
  inputRows,
  columns,
  additionnalsComponents,
  additionnalsLeftComponents,
  onRowClickMethod,
  onCellClickMethod,
  selection,
  clickable,
  isRowSelectableMethod,
  isAnswerFilterActive,
  setAnswerFilterActive,
  hideHeader,
  sortModel,
  onSortModelChange,
  pageSize = 50,
  answerBadgeCount = 0,
  showAnswerFilter = false,
  initialVisibilityModel,
  hasFilters = true,
  updateVisibilityModel,
  initialFilterModel,
  hasCustomPagination = false,
  variant = 'default',
  customSx,
  externalExportHandler,
  onVisibleRowsCountChange = () => {},
  paginationMode = 'client',
  LoadingOverlay = LinearProgress,
  quickFilter = false,
  sortingOrder = ['desc', 'asc'],
  showExport = true,
  noFilter = false,
  apiRef: externalApiRef,
  paginationType = "PAGINATION",
  density="compact",
  ...other
}) {
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState(
    initialVisibilityModel
  );
  const [state, dispatch] = useContext(Context);
  const [pSize, setPSize] = React.useState(pageSize); // used for multi-datagrid
  const [isFilterActive, setFilterActive] = React.useState(false);
  const [filterButtonEl, setFilterButtonEl] = React.useState(null);
  const [columnsButtonEl, setColumnsButtonEl] = React.useState(null);
  const [visibleRowsCount, setVisibleRowsCount] = React.useState(
    inputRows?.length
  );
  const theme = useTheme();

  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? internalApiRef;

  React.useEffect(() => {
    if (hasCustomPagination && paginationMode === 'client') {
      const pagination = {
        page: 0,
        count: visibleRowsCount,
      };
      dispatch({ type: paginationType, [paginationType.toLowerCase()]: pagination });
    }
  }, [visibleRowsCount, paginationMode, hasCustomPagination, dispatch, paginationType]);

  React.useEffect(() => {
    if(paginationMode === "client") {
      const unsubscribe = apiRef.current.subscribeEvent("stateChange", () => {
        const count = gridVisibleRowCountSelector(apiRef.current.state);
        setVisibleRowsCount(count);
        onVisibleRowsCountChange(count)
      });

      return () => {
        unsubscribe?.();
      };
    }
  }, [apiRef, paginationMode, onVisibleRowsCountChange]);

  // Update rowPerPage in pagination state or self managment
  const handleChangeMaxPage = (pageSize) => {
    if (hasCustomPagination) {
      dispatch({
        type: paginationType,
        [paginationType.toLowerCase()]: { ...state[paginationType.toLowerCase()], rowPerPage: pageSize },
      });
    } else {
      setPSize(pageSize); // in case of self managment of data
    }
  };

  // Update page in pagination state
  const handleChangePage = (number) => {
    if (hasCustomPagination) {
      dispatch({
        type: paginationType,
        [paginationType.toLowerCase()]: { ...state[paginationType.toLowerCase()], page: number },
      });
    }
  };

  React.useEffect(() => {
    setColumnVisibilityModel((prevState) => {
      if (_.isEqual(prevState, initialVisibilityModel)) {
        return prevState;
      }
      return initialVisibilityModel;
    });
  }, [initialVisibilityModel]);

  // Récupère la couleur des lignes sélectionnées du theme actuel
  let selectedRowStyle = {
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: state.personalization.isCustomTemplate
        ? theme.palette.primary.primary50 + ' !important'
        : theme.palette.primary.primary100, // primary100
    },
  };

  const handleSelectionModelChange = React.useCallback((newSelectionModel) => {
    selection.setItems(newSelectionModel);
  }, [selection]);

  return (
      <div style={{ height: "100%", width: "100%", overflow: "hidden" }}>
        <DataGridPremium
          apiRef={apiRef}
          localeText={dataGridTranslation()}
          componentsProps={{
            pagination: {
              labelRowsPerPage: i18next.t('dataGrid.linesByPage'),
              labelDisplayedRows: defaultLabelDisplayedRows,
            },
            panel: {
              anchorEl: isFilterActive ? filterButtonEl : columnsButtonEl,
              placement: "bottom-end"
            },
            toolbar: {
              setColumnsButtonEl,
              setFilterButtonEl,
              setFilterActive,
              setAnswerFilterActive,
              isAnswerFilterActive,
              additionnalsComponents,
              additionnalsLeftComponents,
              answerBadgeCount,
              showAnswerFilter,
              hasFilters,
              externalExportHandler,
              quickFilter,
              showExport,
              noFilter,
            },
          }}
          sx={
            variant === "sheet"
              ? { ...dataGridSx, ...customSx, ...sheetStyle, ...selectedRowStyle}
              : { ...dataGridSx, ...customSx, ...selectedRowStyle }
          }
          className={"" + (clickable ? "clickable" : "")}
          loading={loading}
          density={density}
          disableSelectionOnClick
          autoHeight
          pageSize={
            hasCustomPagination
              ? state[paginationType.toLowerCase()].rowPerPage
              : pSize
          }
          rowsPerPageOptions={[10, 50, 100]}
          onPageSizeChange={(pageSize) => handleChangeMaxPage(pageSize)}
          onPageChange={(number) => handleChangePage(number)}
          pagination
          rows={inputRows}
          columns={columns}
          onCellClick={onCellClickMethod}
          onRowClick={onRowClickMethod}
          sortingMode={paginationMode}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          sortingOrder={sortingOrder}
          {...(columnVisibilityModel ? { columnVisibilityModel } : {})}
          onColumnVisibilityModelChange={(newModel, detail) => {
            setColumnVisibilityModel(newModel);
            if(updateVisibilityModel) {
              updateVisibilityModel(newModel);
            }
          }}
          {...(isRowSelectableMethod
            ? {
              isRowSelectable: isRowSelectableMethod,
            }
            : {})}
          {...(selection
            ? {
              checkboxSelection: !selection.hideCheckbox,
              selectionModel: selection.items,
              onSelectionModelChange: handleSelectionModelChange,
              keepNonExistentRowsSelected: paginationMode === 'server',
            }
          : {})}
        {...(!hideHeader
          ? {
              components: {
                Toolbar: CustomToolbar,
                LoadingOverlay: LoadingOverlay,
              },
            }
          : {})}
        {...(initialFilterModel && hasFilters
          ? {
              initialState: {
                filter: {
                  filterModel: initialFilterModel,
                },
              },
            }
          : {})}
        {...(hasCustomPagination
          ? {
              page: state[paginationType.toLowerCase()].page,
              components: {
                Toolbar: CustomToolbar,
                LoadingOverlay: LinearProgress,
                Pagination: '',
              },
            }
          : {
              // components: {
              //   Toolbar: CustomToolbar,
              //   LoadingOverlay: LinearProgress,
              //   Pagination: EAPagination
              // }
            })}
        paginationMode={paginationMode}
        {...other}
      />
    </div>
  );
}
